import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import tinycolor from "tinycolor2";
import EmptyState from '../components/CommonComponents/EmptyState';
import { AlertCircle, ArrowRight, BarChart3, Building2, CalendarDays, ShoppingCart, Wallet } from 'lucide-react';
import { useTheme } from "../components/CommonComponents/ThemeProvider";
import MonthPicker from "../components/CommonComponents/MonthPicker";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/orders/analytics`;

// add a constant for low-stock threshold
const LOW_STOCK_THRESHOLD = parseInt(process.env.REACT_APP_LOW_STOCK_THRESHOLD) || 10;

const createBranchSelectStyles = (themeColor) => {
  const hoverTint = tinycolor(themeColor).setAlpha(0.08).toRgbString();

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 42,
      borderColor: state.isFocused ? themeColor : "#e2e8f0",
      boxShadow: state.isFocused ? `0 0 0 4px ${tinycolor(themeColor).setAlpha(0.14).toRgbString()}` : "0 10px 25px rgba(15,23,42,0.05)",
      borderRadius: 14,
      "&:hover": { borderColor: themeColor },
      fontSize: 13,
      backgroundColor: "#fff",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 6px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 16,
      border: "1px solid #e2e8f0",
      overflow: "hidden",
      zIndex: 80,
      boxShadow: "0 18px 45px rgba(15,23,42,0.14)",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 120 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? themeColor
        : state.isFocused
          ? hoverTint
          : "white",
      color: state.isSelected ? "#fff" : "#111827",
      fontSize: 14,
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6b7280",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? themeColor : "#6b7280",
      "&:hover": {
        color: themeColor,
      },
    }),
  };
};

// Utility function to format currency
const formatCurrency = (amount) => {
  if (isNaN(amount)) return "₱0.00";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount);
};

const getLocalDateKey = (date = new Date()) => date.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
const getLocalMonthKey = (date = new Date()) => getLocalDateKey(date).slice(0, 7);

const formatMonthLabel = (monthKey) => {
  const [rawYear, rawScope] = String(monthKey || "").split("-");
  if (/^\d{4}$/.test(rawYear) && rawScope === "all") return `All ${rawYear}`;
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return "Current Month";
  return new Date(year, month - 1, 1).toLocaleDateString("en-PH", {
    month: "long",
    year: "numeric",
  });
};

const parseLocalDateKey = (dateKey) => {
  if (!dateKey) return null;
  const [year, month, day] = String(dateKey).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const DashboardSkeleton = () => (
  <div className="min-h-screen bg-gray-50 pb-5 pt-2 sm:pb-6 sm:pt-3">
    <div className="flex animate-pulse flex-col gap-6">
      <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-slate-100 blur-3xl" />
          <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
        </div>
        <div className="relative flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 space-y-4">
            <div className="h-8 w-44 rounded-full bg-slate-100" />
            <div className="h-11 w-60 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
            <div className="space-y-2">
              <div className="h-4 w-full max-w-xl rounded-full bg-slate-100" />
              <div className="h-4 w-80 max-w-full rounded-full bg-slate-100" />
            </div>
          </div>
          <div className="w-full rounded-[24px] border border-white/70 bg-white/85 p-4 shadow-[0_10px_28px_rgba(15,23,42,0.06)] xl:max-w-md">
            <div className="h-4 w-32 rounded-full bg-slate-100" />
            <div className="mt-3 h-12 rounded-2xl bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200" />
            <div className="mt-3 h-3 w-44 rounded-full bg-slate-100" />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="h-3 w-32 rounded-full bg-slate-100" />
                <div className="mt-4 h-8 w-36 rounded-full bg-slate-200" />
                <div className="mt-2 h-3 w-24 rounded-full bg-slate-100" />
              </div>
              <div className="h-11 w-11 rounded-2xl bg-slate-100" />
            </div>
            {idx > 1 && <div className="mt-4 h-3 w-24 rounded-full bg-slate-100" />}
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6 lg:col-span-2">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="h-5 w-32 rounded-full bg-slate-200" />
              <div className="h-3 w-52 rounded-full bg-slate-100" />
            </div>
            <div className="h-8 w-24 rounded-full bg-slate-100" />
          </div>
          <div className="h-60 rounded-[24px] bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
            <div className="flex h-full items-end gap-3">
              {[55, 72, 46, 88, 58, 70, 52].map((height, idx) => (
                <div key={idx} className="flex flex-1 items-end">
                  <div
                    className="w-full rounded-t-2xl bg-gradient-to-t from-slate-300 via-slate-200 to-slate-100"
                    style={{ height: `${height}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="h-5 w-28 rounded-full bg-slate-200" />
              <div className="h-3 w-40 rounded-full bg-slate-100" />
            </div>
            <div className="h-8 w-16 rounded-full bg-slate-100" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-4/5 rounded-full bg-slate-200" />
                  <div className="h-2.5 w-2/5 rounded-full bg-slate-100" />
                </div>
                <div className="ml-4 h-7 w-10 rounded-full bg-white" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <div className="h-5 w-40 rounded-full bg-slate-200" />
            <div className="h-3 w-72 max-w-full rounded-full bg-slate-100" />
          </div>
          <div className="h-9 w-24 rounded-full bg-slate-100" />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="rounded-[22px] border border-slate-100 bg-slate-50/70 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="h-4 w-40 rounded-full bg-slate-200" />
                  <div className="h-3 w-24 rounded-full bg-slate-100" />
                </div>
                <div className="h-8 w-16 rounded-full bg-white" />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  </div>
);

export default function Dashboard() {
  const { currentTheme } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [totalLowStockCount, setTotalLowStockCount] = useState(0);
  const [todayExpenseTotal, setTodayExpenseTotal] = useState(0);
  const [todayExpenseCount, setTodayExpenseCount] = useState(0);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(getLocalMonthKey());
  const branchSelectStyles = createBranchSelectStyles(currentTheme.color);

  const branchOptions = [
    { value: "", label: "All Branches" },
    ...branches.map((branch) => ({
      value: String(branch.id),
      label: branch.name || branch.location || `Branch ${branch.id}`,
    })),
  ];

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");

      let url = API_URL;
      const params = new URLSearchParams();
      if (customerId) params.set("customer_id", customerId);
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      if (selectedMonth) params.set("month", selectedMonth);
      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId, selectedMonth]);

  // Fetch low stock items
  const fetchLowStock = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const params = new URLSearchParams({
        threshold: String(LOW_STOCK_THRESHOLD + 1),
      });
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/inventory/low-stock?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const allLow = Array.isArray(data) ? data : [];
      setTotalLowStockCount(allLow.length);
      setLowStockItems(allLow.slice(0, 5));
    } catch (err) {
      console.error("Failed to fetch low stock:", err);
      setTotalLowStockCount(0);
      setLowStockItems([]);
    }
  }, [selectedBranchId]);

  // Fetch today's expenses total for admin dashboard card
  const fetchTodayExpenses = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const todayLocal = getLocalDateKey();
      const params = new URLSearchParams({
        startDate: todayLocal,
        endDate: todayLocal,
      });
      if (selectedBranchId) params.set("branch_id", selectedBranchId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/pos/expenses?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      const expenseList = Array.isArray(data) ? data : [];
      const total = expenseList.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      setTodayExpenseTotal(total);
      setTodayExpenseCount(expenseList.length);
    } catch (err) {
      console.error("Failed to fetch today's expenses:", err);
      setTodayExpenseTotal(0);
      setTodayExpenseCount(0);
    }
  }, [selectedBranchId]);

  const fetchDashboardBranches = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");
      const params = new URLSearchParams();
      if (customerId) params.set("customer_id", customerId);
      const queryString = params.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/branches${queryString ? `?${queryString}` : ""}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      const branchList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setBranches(
        branchList.filter((branch) => String(branch?.status || "").toUpperCase() === "ACTIVE")
      );
    } catch (err) {
      console.error("Failed to fetch branches:", err);
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    fetchDashboardBranches();
    fetchAnalytics();
    fetchLowStock();
    fetchTodayExpenses();
  }, [fetchDashboardBranches, fetchAnalytics, fetchLowStock, fetchTodayExpenses]);

  if (error) {
    return (
      <div className="p-6">
        <EmptyState
          icon={BarChart3}
          title="Failed to Load Dashboard"
          message="Check your internet connection and try again."
          onRetry={fetchAnalytics}
          retryLabel="Retry"
        />
      </div>
    );
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Calculate metrics using Manila calendar dates returned by the analytics API.
  const today = analytics?.today || { totalSales: 0, ordersCount: 0 };

  const totalSalesMonth = Number(analytics?.total_sales || 0);
  const totalOrdersMonth = Number(analytics?.total_orders || 0);
  const selectedMonthLabel = formatMonthLabel(selectedMonth);

  const sortedMonthDays = [...(analytics?.salesByDay || [])].sort((a, b) => {
    const da = parseLocalDateKey(a.date);
    const db = parseLocalDateKey(b.date);
    return da - db;
  });
  const chartLabels = sortedMonthDays.map(d => String(d.date || "").slice(0, 10));
  const chartData = sortedMonthDays.map(d => parseFloat(d.totalSales));
  const selectedBranchLabel = branchOptions.find((option) => option.value === selectedBranchId)?.label || "All Branches";
  const kpiCards = [
    {
      label: "Today's Gross Sales",
      value: formatCurrency(today.totalSales),
      hint: `${today.ordersCount} transactions`,
      icon: ShoppingCart,
      tone: "blue",
    },
    {
      label: `${selectedMonthLabel} Sales`,
      value: formatCurrency(totalSalesMonth),
      hint: `${totalOrdersMonth} orders`,
      icon: BarChart3,
      tone: "green",
    },
    {
      label: "Today's Expenses",
      value: formatCurrency(todayExpenseTotal),
      hint: `${todayExpenseCount} entries`,
      icon: Wallet,
      tone: "amber",
      to: "/admin/expenses",
    },
    {
      label: "Low Stock",
      value: totalLowStockCount,
      hint: `Threshold: ${LOW_STOCK_THRESHOLD}`,
      icon: AlertCircle,
      tone: "red",
      to: "/admin/inventory",
    },
  ];
  const toneStyles = {
    blue: "border-blue-100 bg-blue-50 text-blue-700",
    green: "border-emerald-100 bg-emerald-50 text-emerald-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    red: "border-red-100 bg-red-50 text-red-700",
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-5 pt-2 sm:pb-6 sm:pt-3">
      <div className="flex flex-col gap-6">
        <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
            <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
          </div>
          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0 xl:flex-1">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
                <BarChart3 className="h-3.5 w-3.5" />
                Store Overview
              </div>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Dashboard</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
                Monitor sales, expenses, inventory risk, and branch performance from one workspace.
              </p>
            </div>
            <div className="grid w-full gap-2.5 rounded-[22px] border border-white/70 bg-white/85 p-3 shadow-[0_10px_28px_rgba(15,23,42,0.06)] md:grid-cols-2 xl:w-[520px] xl:max-w-[42%]">
              <div className="min-w-0">
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  <Building2 className="h-3.5 w-3.5" />
                  Branch Scope
                </div>
                <Select
                  classNamePrefix="react-select"
                  styles={branchSelectStyles}
                  options={branchOptions}
                  value={branchOptions.find((option) => option.value === selectedBranchId) || branchOptions[0]}
                  onChange={(option) => setSelectedBranchId(option?.value || "")}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                <p className="mt-1.5 truncate text-[11px] text-slate-500">Current view: {selectedBranchLabel}</p>
              </div>
              <div className="min-w-0">
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Month Scope
                </div>
                <MonthPicker
                  value={selectedMonth}
                  onChange={(nextMonth) => setSelectedMonth(nextMonth || getLocalMonthKey())}
                  themeColor={currentTheme.color}
                  ariaLabel="Select dashboard month"
                  includeAll
                />
                <p className="mt-1.5 truncate text-[11px] text-slate-500">Reports scoped to {selectedMonthLabel}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {kpiCards.map(({ label, value, hint, icon: Icon, tone, to }) => {
            const content = (
              <div className="group h-full rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(15,23,42,0.09)]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
                    <p className="mt-3 truncate text-2xl font-semibold text-slate-950">{value}</p>
                    <p className="mt-1 text-xs text-slate-500">{hint}</p>
                  </div>
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${toneStyles[tone]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                {to && (
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 transition group-hover:text-slate-900">
                    Open details <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            );
            return to ? <Link key={label} to={to}>{content}</Link> : <div key={label}>{content}</div>;
          })}
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Sales Trend</h2>
                <p className="mt-1 text-sm text-slate-500">Daily gross sales for {selectedMonthLabel}.</p>
              </div>
              <Link to="/admin/analytics" className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50">
                View full <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          <div className="h-60">
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: 'Sales',
                    data: chartData,
                    borderColor: currentTheme.color,
                    backgroundColor: tinycolor(currentTheme.color).setAlpha(0.08).toRgbString(),
                    pointBackgroundColor: currentTheme.color,
                    pointRadius: 4,
                    tension: 0.4,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { callback: v => `₱${v}` },
                  },
                },
              }}
            />
          </div>
          </div>

          <div className="rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Top Products</h2>
                <p className="mt-1 text-sm text-slate-500">Best-selling items in {selectedMonthLabel}.</p>
              </div>
              <Link to="/admin/analytics" className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50">
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="space-y-3">
            {(analytics?.topProducts || []).slice(0, 5).map((product, idx) => (
              <div key={idx} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: "var(--color-primary)" }}>
                    {idx + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                    {product.product || `Product ${product.product_id}`}
                  </p>
                    <p className="text-xs text-slate-500">{product.totalQty} units sold</p>
                  </div>
                </div>
                <div className="text-sm font-semibold text-slate-900">{product.totalQty}</div>
              </div>
            ))}
            {(analytics?.topProducts || []).length === 0 && (
              <p className="rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-6 text-center text-sm text-slate-500">No top products yet.</p>
            )}
            </div>
          </div>
        </section>

        <section className="mb-6 rounded-[30px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.07)] sm:p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-600">
                <AlertCircle className="h-3.5 w-3.5" />
                Inventory Watch
              </div>
              <h2 className="mt-3 text-lg font-semibold text-slate-950">
              Low Stock Items ({totalLowStockCount})
            </h2>
            </div>
            <Link to="/admin/inventory" className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {lowStockItems.length === 0 ? (
            <div className="rounded-[24px] border border-emerald-100 bg-emerald-50 px-4 py-6 text-center text-sm font-semibold text-emerald-700">
              All items well stocked
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {lowStockItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between gap-3 rounded-[22px] border border-slate-200 bg-slate-50/70 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {item.product_name || item.product?.name || item.name || `Product ${item.id}`}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {item.branch_name || item.branch?.name || 'Unknown Branch'}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${item.quantity <= 3
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {item.quantity} left
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
